import React, { createContext, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDashboardPermissions } from '@components/utils/DashboardPermissions';
import { routing } from '@utils/routing';
import { useAccountSettingsQuery } from '../../Settings/hooks/useAccountSettingsQuery';
import { FREE_TIER_ID } from '../consts';
import { useFreeTierQuery } from '../hooks/useFreeTierQuery';
import { useReorderFreeTierMutation } from '../hooks/useReorderFreeTierMutation';
import { useReorderTierMutation } from '../hooks/useReorderTierMutation';
import { useTierActions } from '../hooks/useTierActions';
import { useTiersQuery } from '../hooks/useTiersQuery';
export const TiersContext = createContext(undefined);
export const TiersProvider = ({ children }) => {
    const navigate = useNavigate();
    const { hasPermission } = useDashboardPermissions();
    const { tiersQuery } = useTiersQuery();
    const { freeTierQuery } = useFreeTierQuery();
    const { accountSettingsQuery } = useAccountSettingsQuery();
    const { reorderTierMutation } = useReorderTierMutation();
    const { reorderFreeTierMutation } = useReorderFreeTierMutation();
    const tierActions = useTierActions();
    const isLoading = tiersQuery.isFetching ||
        freeTierQuery.isFetching ||
        accountSettingsQuery.isFetching ||
        reorderTierMutation.isLoading ||
        reorderFreeTierMutation.isLoading ||
        tierActions.isLoading;
    const addTier = useCallback(() => {
        navigate(routing.memberships.perks.create);
    }, [navigate]);
    const publishTier = useCallback((tier) => {
        return tierActions.publishTier(tier, () => {
            tiersQuery.refetch();
            freeTierQuery.refetch();
        });
    }, [tierActions, tiersQuery, freeTierQuery]);
    const unpublishTier = useCallback((tier) => {
        return tierActions.unpublishTier(tier, () => {
            tiersQuery.refetch();
            freeTierQuery.refetch();
        });
    }, [tierActions, tiersQuery, freeTierQuery]);
    const sortTier = useCallback((tier, newPosition) => {
        reorderTierMutation.mutate([tier.id, { position: newPosition }], {
            onSuccess: () => {
                tiersQuery.refetch();
                freeTierQuery.refetch();
            },
        });
    }, [reorderTierMutation, tiersQuery, freeTierQuery]);
    const sortFreeTier = useCallback((newPosition) => {
        reorderFreeTierMutation.mutate([{ position: newPosition }], {
            onSuccess: () => {
                tiersQuery.refetch();
                freeTierQuery.refetch();
            },
        });
    }, [reorderFreeTierMutation, freeTierQuery, tiersQuery]);
    const deleteTier = useCallback((tier) => {
        return tierActions.deleteTier(tier, () => {
            tiersQuery.refetch();
            freeTierQuery.refetch();
        });
    }, [tierActions, tiersQuery, freeTierQuery]);
    const deleteFreeTier = useCallback(() => {
        return tierActions.deleteFreeTier(() => {
            tiersQuery.refetch();
            freeTierQuery.refetch();
        });
    }, [tierActions, freeTierQuery, tiersQuery]);
    const tiers = useMemo(() => {
        if (!tiersQuery.data)
            return [];
        if (!freeTierQuery.data)
            return [];
        if (freeTierQuery.data.status === 'Hidden') {
            return tiersQuery.data.tiers.sort((a, b) => a.position - b.position);
        }
        // Note: Paid and free tier could have the same id, so we need to add a unique id to the free tier
        return [...tiersQuery.data.tiers, { ...freeTierQuery.data, id: FREE_TIER_ID }].sort((a, b) => a.position - b.position);
    }, [freeTierQuery.data, tiersQuery.data]);
    const value = useMemo(() => ({
        isLoading,
        tiers,
        addTier: hasPermission('memberships.perksAndTiers.createTier') ? addTier : undefined,
        deleteFreeTier,
        publishTier,
        unpublishTier,
        sortTier,
        sortFreeTier,
        deleteTier,
    }), [
        isLoading,
        tiers,
        hasPermission,
        addTier,
        publishTier,
        unpublishTier,
        sortTier,
        sortFreeTier,
        deleteTier,
        deleteFreeTier,
    ]);
    return React.createElement(TiersContext.Provider, { value: value }, children);
};
